import type { TRangeValue, TSelectItem } from 'src/components/Search/components/types';
import {
    ARCHIVED_CONVERSATION_STATUS,
    BLOCKED_CONVERSATION_STATUS,
    getConversationListFilters,
    type TConversationListFilter,
} from './conversationStatus';

export type TConversationStatusFilter =
    | ''
    | 'active'
    | 'archived'
    | 'blocked';

export type TConversationAdvancedFilters = {
    name: string;
    phone: string;
    email: string;
    store: TSelectItem[];
    assignedTo: TSelectItem[];
    tags: TSelectItem[];
    areas: TSelectItem[];
    status: TConversationStatusFilter;
    createdAt: TRangeValue;
    lastMessageAt: TRangeValue;
};

export const DEFAULT_CONVERSATION_ADVANCED_FILTERS: TConversationAdvancedFilters =
    {
        name: '',
        phone: '',
        email: '',
        store: [],
        assignedTo: [],
        tags: [],
        areas: [],
        status: '',
        createdAt: null,
        lastMessageAt: null,
    };

const toDateRangePayload = (range: TRangeValue) => {
    if (!range?.gte || !range?.lt) return undefined;

    return {
        gte: range.gte,
        lt: range.lt,
    };
};

const toInFilter = (items: TSelectItem[]) => {
    if (!items.length) return undefined;
    if (items.length === 1) return items[0].value;

    return { $in: items.map((item) => item.value) };
};

const getAdvancedStatusFilter = (status: TConversationStatusFilter) => {
    if (status === 'archived') return ARCHIVED_CONVERSATION_STATUS;
    if (status === 'blocked') return BLOCKED_CONVERSATION_STATUS;
    if (status === 'active') {
        return {
            $nin: [ARCHIVED_CONVERSATION_STATUS, BLOCKED_CONVERSATION_STATUS],
        };
    }

    return undefined;
};

export const hasActiveAdvancedFilters = (
    filters: TConversationAdvancedFilters
): boolean =>
    Boolean(
        filters.name.trim() ||
            filters.phone.trim() ||
            filters.email.trim() ||
            filters.store.length ||
            filters.assignedTo.length ||
            filters.tags.length ||
            filters.areas.length ||
            filters.status ||
            filters.createdAt ||
            filters.lastMessageAt
    );

/**
 * Builds the filters object sent to POST /conversations (flattened into the body).
 * Maps UI advanced filters + list tab + free-text query to WarboxConversation fields.
 */
export const buildConversationFiltersPayload = ({
    listFilter,
    advancedFilters,
    query,
    area,
}: {
    listFilter: TConversationListFilter;
    advancedFilters: TConversationAdvancedFilters;
    query?: string;
    area?: string | { $in: string[] };
}): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};

    const advancedStatus = getAdvancedStatusFilter(advancedFilters.status);
    if (advancedStatus !== undefined) {
        payload.status = advancedStatus;
    } else {
        Object.assign(payload, getConversationListFilters(listFilter).filters);
    }

    const trimmedQuery = query?.trim();
    if (trimmedQuery) payload.query = trimmedQuery;

    const name = advancedFilters.name.trim();
    if (name) payload['contact.displayName'] = name;

    const phone = advancedFilters.phone.trim();
    if (phone) payload['contact.phone'] = phone;

    const email = advancedFilters.email.trim();
    if (email) payload['contact.email'] = email;

    const store = toInFilter(advancedFilters.store);
    if (store !== undefined) payload.store = store;

    const assignedTo = toInFilter(advancedFilters.assignedTo);
    if (assignedTo !== undefined) payload.assignedTo = assignedTo;

    if (advancedFilters.tags.length) {
        payload['tags.name'] = {
            $in: advancedFilters.tags.map((tag) => tag.value),
        };
    }

    const createdAt = toDateRangePayload(advancedFilters.createdAt);
    if (createdAt) payload.createdAt = createdAt;

    const lastMessageAt = toDateRangePayload(advancedFilters.lastMessageAt);
    if (lastMessageAt) payload.lastMessageAt = lastMessageAt;

    if (area !== undefined) payload['area.name'] = area;

    return payload;
};
